import { __awaiter } from "tslib";
import { Color, DoubleSide, MirroredRepeatWrapping, RepeatWrapping, Texture, Vector2, } from "three";
import { computeBoundingBoxHelper } from "./computeBoundingBoxHelper";
export class DivisorMaterial {
    constructor(isSolid = false) {
        this._isSolid = false;
        this._texturesLoaded = false;
        this._color = new Color("#ffffff");
        this._belowColor = new Color("#ffffff");
        this._lowerSideAttrs = undefined;
        this._isSolid = isSolid;
        this._preloadImages().then(() => {
            this._texturesLoaded = true;
        });
    }
    get texturesLoaded() {
        return this._texturesLoaded;
    }
    get isSolid() {
        return this._isSolid;
    }
    get color() {
        return this._color;
    }
    set color(v) {
        this._color = new Color(v);
    }
    get belowColor() {
        return this._belowColor;
    }
    set belowColor(v) {
        this._belowColor = new Color(v);
    }
    applyToCombo(obj3D1, obj3D2, cells, tiers) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!obj3D1)
                return;
            const cells1 = cells, cells2 = cells / 2;
            let tiers1 = tiers, tiers2 = tiers * 0.34;
            if (tiers === 0) {
                //Calculate height automatically
                const bboxHelper = computeBoundingBoxHelper(obj3D1);
                const ratioSep = bboxHelper ? bboxHelper.size.y / bboxHelper.size.z : 1;
                tiers1 = ratioSep * cells;
                tiers2 = tiers1 * 0.34;
            }
            yield awaitTillReady(this._texturesLoaded, 50);
            useTextures(this.color, this.belowColor);
            function attachTextureToMesh(obj, texturePartName, c, t, color, moveY = 0, centerY = 0) {
                if (!obj)
                    return;
                const tCache = Math.round(t);
                let material = DivisorMaterial._materialsCache[`${texturePartName}-${c}-${tCache}-${color.getHex()}`];
                if (material) {
                    obj.material = material;
                    return;
                }
                const tDif = new Texture(DivisorMaterial.imagesCache[texturePartName + "-Diffuse.png"]);
                tDif.wrapS = MirroredRepeatWrapping;
                tDif.wrapT = RepeatWrapping;
                tDif.anisotropy = 4;
                tDif.flipY = false;
                tDif.repeat = new Vector2(c, t);
                tDif.center = new Vector2(0, centerY);
                tDif.offset = new Vector2(0, moveY);
                tDif.needsUpdate = true;
                const tNrm = new Texture(DivisorMaterial.imagesCache[texturePartName + "-Normal.png"]);
                tNrm.wrapS = MirroredRepeatWrapping;
                tNrm.wrapT = RepeatWrapping;
                tNrm.anisotropy = 4;
                tNrm.flipY = false;
                tNrm.repeat = new Vector2(c, t);
                tNrm.center = new Vector2(0, centerY);
                tNrm.offset = new Vector2(0, moveY);
                tNrm.needsUpdate = true;
                material = obj.material.clone();
                material.map = tDif;
                material.normalMap = tNrm;
                material.transparent = true;
                material.alphaTest = 0.5;
                material.color.set(color);
                material.side = DoubleSide;
                material.needsUpdate = true;
                DivisorMaterial._texturesCache[`${texturePartName}-${c}-${tCache}-dif`] =
                    tDif;
                DivisorMaterial._texturesCache[`${texturePartName}-${c}-${tCache}-nrm`] =
                    tNrm;
                DivisorMaterial._materialsCache[`${texturePartName}-${c}-${tCache}-${color.getHex()}`] = material;
                obj.material = material;
            }
            function useTextures(color, belowColor) {
                if (obj3D2) {
                    attachTextureToMesh(obj3D1, "Fence", cells1, tiers1, color);
                    attachTextureToMesh(obj3D2, "Diagonal", cells2, tiers2, color, -0.018);
                }
                else {
                    attachTextureToMesh(obj3D1, "Fence-Lower", cells1, tiers1, belowColor, 0, 0);
                }
            }
        });
    }
    colorizeSide(obj3DMaterial, color) {
        const c = new Color(color);
        const opaq = new Color(0x808080);
        c.multiply(opaq);
        obj3DMaterial.color.set(c);
    }
    _preloadImages() {
        return __awaiter(this, void 0, void 0, function* () {
            const texturesPath = DivisorMaterial.imagesRoute;
            const IMAGES_LIST = [
                "Diagonal-Diffuse.png",
                "Diagonal-Normal.png",
                "Fence-Diffuse.png",
                "Fence-Normal.png",
                "Fence-Lower-Diffuse.png",
                "Fence-Lower-Normal.png",
            ];
            yield Promise.all(IMAGES_LIST.map(preloadImage));
            function preloadImage(src) {
                if (DivisorMaterial.imagesCache[src]) {
                    return Promise.resolve(DivisorMaterial.imagesCache[src]);
                }
                return new Promise((resolve, reject) => {
                    const img = new Image();
                    img.onerror = function (e) {
                        console.error(e);
                        reject(e);
                    };
                    img.onload = function () {
                        DivisorMaterial.imagesCache[src] = img;
                        resolve(img);
                    };
                    img.src = texturesPath + src;
                });
            }
        });
    }
    setUvs(geometry, alpha) {
        if (!this._lowerSideAttrs) {
            this._lowerSideAttrs = geometry.attributes["uv"].clone();
        }
        const uvAttribute1 = this._lowerSideAttrs, uvAttribute2 = geometry.attributes["uv2"];
        let u1, v1, u2, v2, u, v;
        for (let i = 0; i < uvAttribute1.count; i++) {
            u1 = uvAttribute1.getX(i);
            v1 = uvAttribute1.getY(i);
            u2 = uvAttribute2.getX(i);
            v2 = uvAttribute2.getY(i);
            u = u1 * (1 - alpha) + u2 * alpha;
            v = v1 * (1 - alpha) + v2 * alpha;
            geometry.attributes["uv"].setXY(i, u, v);
        }
        geometry.attributes["uv"].needsUpdate = true;
    }
}
DivisorMaterial.imagesCache = {};
DivisorMaterial._materialsCache = {};
DivisorMaterial._texturesCache = {};
DivisorMaterial.imagesRoute = "/assets/vessels/textures/";
function awaitTillReady(condition_1) {
    return __awaiter(this, arguments, void 0, function* (condition, retryMs = 50) {
        return new Promise((resolve) => {
            if (condition) {
                resolve();
            }
            else {
                const timer = window.setTimeout(() => {
                    if (condition) {
                        window.clearInterval(timer);
                        resolve();
                    }
                }, retryMs);
            }
        });
    });
}
